// ROOM 404: Student Ideas & Community Proposals Interface
// Verified members submit freely without admin approval; volunteer signups, discussions, and event conversions.

import React, { useEffect, useState } from 'react';
import {
  Calendar,
  CheckCircle,
  EyeOff,
  HandMetal,
  Heart,
  Lightbulb,
  MessageSquare,
  Plus,
  Sparkles,
  Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/api';
import type { CommunityIdea } from '../types';

export const Ideas: React.FC = () => {
  const { session, isStaff } = useAuth();
  const [ideas, setIdeas] = useState<CommunityIdea[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<CommunityIdea | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('social');
  const [suggestedDate, setSuggestedDate] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('Free');
  const [suggestedGroupSize, setSuggestedGroupSize] = useState('5-15');
  const [isIndoor, setIsIndoor] = useState(true);
  const [assistanceNeeded, setAssistanceNeeded] = useState('');
  const [isAliasHidden, setIsAliasHidden] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fetchIdeas = async () => {
    try {
      const data = await apiRequest<{ ideas: CommunityIdea[] }>('/api/ideas');
      setIdeas(data.ideas);
    } catch {
      // Ignored
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIdeas();
  }, []);

  const handleToggleInterest = async (ideaId: string, isVolunteer = false) => {
    try {
      const res = await apiRequest<{ interested: boolean }>(`/api/ideas/${ideaId}/interest`, {
        method: 'POST',
        body: { isVolunteer },
      });

      setIdeas((prev) =>
        prev.map((item) => {
          if (item.id === ideaId) {
            return {
              ...item,
              user_has_interest: res.interested,
              interest_count: res.interested ? item.interest_count + 1 : Math.max(0, item.interest_count - 1),
            };
          }
          return item;
        })
      );
    } catch {
      // Ignored
    }
  };

  const handleOpenComments = async (idea: CommunityIdea) => {
    setSelectedIdea(idea);
    try {
      // Using existing comments or empty
      setComments([]);
    } catch {
      // Ignored
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIdea || !newComment.trim()) return;

    try {
      const res = await apiRequest<{ success: boolean; comment: any }>(
        `/api/ideas/${selectedIdea.id}/comments`,
        {
          method: 'POST',
          body: { body: newComment.trim() },
        }
      );

      setComments((prev) => [...prev, res.comment]);
      setNewComment('');
    } catch {
      // Ignored
    }
  };

  const handleSubmitIdea = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await apiRequest('/api/ideas', {
        method: 'POST',
        body: {
          title: title.trim(),
          description: description.trim(),
          category,
          suggestedDate: suggestedDate || undefined,
          estimatedCost: estimatedCost || undefined,
          suggestedGroupSize: suggestedGroupSize || undefined,
          isIndoor,
          assistanceNeeded: assistanceNeeded || undefined,
          isAliasHidden,
        },
      });

      setShowSubmitModal(false);
      setTitle('');
      setDescription('');
      fetchIdeas();
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to submit proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConvertToEvent = async (ideaId: string) => {
    if (!confirm('Convert this community favourite proposal into an official society event?')) return;
    try {
      await apiRequest(`/api/ideas/${ideaId}/convert-to-event`, { method: 'POST' });
      alert('Event approved! Planning room created and 100 points awarded to the submitter.');
      fetchIdeas();
    } catch (err: any) {
      alert(err.message || 'Conversion failed');
    }
  };

  return (
    <div className="space-y-3 pb-24 max-w-md mx-auto px-4 pt-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white tracking-tight">Community Ideas</h2>
          <p className="text-xs text-gray-400">Proposals submit freely without admin approval</p>
        </div>
        <button
          onClick={() => setShowSubmitModal(true)}
          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-black font-semibold text-xs rounded-xl flex items-center space-x-1 transition-all"
        >
          <Plus className="w-3.5 h-3.5 stroke-[3]" />
          <span>New Idea</span>
        </button>
      </div>

      {/* Ideas Feed */}
      <div className="space-y-3">
        {ideas.map((idea) => {
          const isFavourite = idea.status === 'community_favourite' || idea.interest_count >= 10;
          const isApprovedEvent = idea.status === 'approved_event';

          return (
            <div
              key={idea.id}
              className="p-4 rounded-2xl bg-[#12141c] border border-[#1f2433] hover:border-gray-700 transition-colors space-y-3"
            >
              {/* Top metadata */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-semibold text-gray-300">
                    {idea.is_alias_hidden ? (
                      <span className="flex items-center space-x-1 text-gray-500">
                        <EyeOff className="w-3 h-3" />
                        <span>Anonymous</span>
                      </span>
                    ) : (
                      idea.author_alias || 'Member'
                    )}
                  </span>
                  <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    {idea.category}
                  </span>
                </div>

                {isApprovedEvent ? (
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    OFFICIAL EVENT
                  </span>
                ) : isFavourite ? (
                  <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center space-x-1">
                    <Sparkles className="w-3 h-3" />
                    <span>FAVOURITE</span>
                  </span>
                ) : null}
              </div>

              {/* Title & Description */}
              <div>
                <h3 className="text-sm font-bold text-white tracking-tight">{idea.title}</h3>
                <p className="text-xs text-gray-300 mt-1 leading-relaxed">{idea.description}</p>
              </div>

              {/* Tags & Logistics */}
              <div className="flex flex-wrap gap-2 text-[11px] text-gray-400 font-mono">
                {idea.suggested_group_size && (
                  <span className="px-2 py-0.5 rounded bg-[#090a0f] border border-[#1f2433]">
                    👥 {idea.suggested_group_size}
                  </span>
                )}
                {idea.estimated_cost && (
                  <span className="px-2 py-0.5 rounded bg-[#090a0f] border border-[#1f2433]">
                    💰 {idea.estimated_cost}
                  </span>
                )}
                <span className="px-2 py-0.5 rounded bg-[#090a0f] border border-[#1f2433]">
                  {idea.is_indoor ? '🏢 Indoor' : '🌲 Outdoor'}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-2 border-t border-[#1f2433]/70">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleInterest(idea.id, false)}
                    className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                      idea.user_has_interest
                        ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                        : 'bg-[#1a1d28] text-gray-400 hover:text-white'
                    }`}
                  >
                    <Heart className={`w-3.5 h-3.5 ${idea.user_has_interest ? 'fill-rose-400' : ''}`} />
                    <span>{idea.interest_count}</span>
                  </button>

                  <button
                    onClick={() => handleToggleInterest(idea.id, true)}
                    className="flex items-center space-x-1 px-3 py-1.5 rounded-xl text-xs bg-[#1a1d28] hover:bg-[#222736] text-gray-300 font-medium transition-colors"
                  >
                    <HandMetal className="w-3.5 h-3.5 text-amber-400" />
                    <span>Volunteer</span>
                  </button>
                </div>

                {isStaff && isFavourite && !isApprovedEvent && (
                  <button
                    onClick={() => handleConvertToEvent(idea.id)}
                    className="px-2.5 py-1 rounded-lg text-xs bg-cyan-500 hover:bg-cyan-400 text-black font-semibold transition-colors"
                  >
                    Approve Event
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Submit Idea Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-[#12141c] border border-[#1f2433] rounded-2xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-bold text-white mb-1">Submit Community Idea</h3>
            <p className="text-xs text-gray-400 mb-4">
              Submit freely without administrator gatekeeping. Proposals gather interest organically.
            </p>

            {submitError && (
              <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs mb-3">
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmitIdea} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Idea Title</label>
                <input
                  type="text"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Midnight Code Jam & Pizza"
                  className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
                >
                  <option value="social">Social</option>
                  <option value="tech">Tech & Coding</option>
                  <option value="arts">Arts & Music</option>
                  <option value="gaming">Gaming</option>
                  <option value="outdoors">Outdoors</option>
                  <option value="academic">Academic & Study</option>
                  <option value="misc">Miscellaneous</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Description</label>
                <textarea
                  required
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Explain the plan and why members would love it..."
                  className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Estimated Cost</label>
                  <input
                    type="text"
                    value={estimatedCost}
                    onChange={(e) => setEstimatedCost(e.target.value)}
                    placeholder="e.g. Free or ₹100"
                    className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Group Size</label>
                  <input
                    type="text"
                    value={suggestedGroupSize}
                    onChange={(e) => setSuggestedGroupSize(e.target.value)}
                    placeholder="e.g. 5-20"
                    className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="aliasHidden"
                  checked={isAliasHidden}
                  onChange={(e) => setIsAliasHidden(e.target.checked)}
                  className="rounded border-gray-700 bg-gray-900 text-emerald-500"
                />
                <label htmlFor="aliasHidden" className="text-xs text-gray-300">
                  Post with alias hidden (Anonymous Proposal)
                </label>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="flex-1 py-2 px-3 bg-[#1f2433] hover:bg-gray-700 text-gray-300 rounded-xl text-xs font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2 px-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-xl text-xs disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Idea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
